/**
 * Programme identity: a colour each, and the pill that carries it.
 *
 * Broadcasters' logos and wordmarks cannot be used here, so a programme is
 * recognised by hue alone. The five colours sit at the same lightness and
 * chroma and differ only in hue, which keeps them equal in weight — none reads
 * as more important than another — and keeps every one of them legible against
 * the same light surface.
 *
 * The name always stays plain text next to the swatch. The colour is a way to
 * find a programme at a glance, never a stand-in for its brand.
 */

import { el } from './format.js';

export const PROGRAM_COLORS = {
  '4-ristoranti': 'oklch(0.52 0.14 48)',
  'cucine-da-incubo': 'oklch(0.52 0.14 18)',
  'camionisti-in-trattoria': 'oklch(0.52 0.14 95)',
  foodish: 'oklch(0.52 0.14 150)',
  'food-advisor': 'oklch(0.52 0.14 250)',
  // Not a programme: the Michelin guide (`src/guida.js`). Its own deeper,
  // stronger red, so a starred pin is never read as one of Cucine da Incubo.
  'stellati-michelin': 'oklch(0.46 0.19 25)',
};

/** An unknown id gets the muted text colour rather than an invented hue. */
export function programColor(programId) {
  return PROGRAM_COLORS[programId] ?? 'var(--muted)';
}

/**
 * The programme pill. `onClick` makes it a button — that is how a venue's
 * card offers its way through to the programme screen; everywhere else it is
 * static text and should not look pressable.
 */
export function programPill(programId, name, { onClick } = {}) {
  const pill = el(onClick ? 'button' : 'span', 'prog');
  if (onClick) {
    pill.type = 'button';
    pill.addEventListener('click', onClick);
  }

  const colour = programColor(programId);
  pill.style.color = colour;
  pill.style.borderColor = colour;

  const swatch = el('i');
  swatch.style.background = colour;
  pill.append(swatch);
  pill.append(el('span', null, name ?? programId));

  return pill;
}

/** The little colour bar that turns the profile's programme list into a legend. */
export function programBar(programId) {
  const bar = el('i', 'prog-bar');
  bar.style.background = programColor(programId);
  return bar;
}
